import {
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Stack,
	Typography,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import React from 'react';
import { useAuthContext } from '../../../../../../context/auth';
import { dayjs } from '../../../../../../date-util';
import { errorToast, successToast } from '../../../../../Toast';
import { TenantsApi } from '../../../../namespace/api';
import {
	QUERY_KEYS,
	unsuspendWebhook,
	WebhookResponse,
} from '../../../api/webhooks';
import {
	CONFIRM_UNSUSPEND_WEBHOOK_CONFIGURATION_MODAL_CANCEL_TEXT,
	CONFIRM_UNSUSPEND_WEBHOOK_CONFIGURATION_MODAL_CONFIRM_MESSAGE,
	CONFIRM_UNSUSPEND_WEBHOOK_CONFIGURATION_MODAL_DELETE_TEXT,
	CONFIRM_UNSUSPEND_WEBHOOK_CONFIGURATION_MODAL_HEADER_LABEL,
	CONFIRM_UNSUSPEND_WEBHOOK_CONFIGURATION_MODAL_SUSPENDED_ON,
	UNSUSPEND_WEBHOOK_CONFIGURATION_FAILURE,
	UNSUSPEND_WEBHOOK_CONFIGURATION_SUCCESS,
	WEBHOOK_CONFIGURATION_UNSUSPEND_DATE_FORMAT,
} from '../../../data/constants';
import { handleModalClose } from '../../../utils/handleModalClose';

interface Props {
	open: boolean;
	configuredWebhook: WebhookResponse;
	tenantHostname?: string;
	onCancel: () => void;
}

export default function ConfirmUnsuspendWebhookConfigurationModal({
	open,
	configuredWebhook,
	tenantHostname,
	onCancel,
}: Props) {
	const client = useQueryClient();
	const authData = useAuthContext();
	const authToken = authData?.tenantAuthorizationData?.authorizationToken;
	const authTenantId = authData?.tenantAuthorizationData?.tenantId;
	const { mutate: onUnsuspend, isLoading } = useMutation(
		() => {
			return tenantHostname
				? TenantsApi.unsuspendWebhook(configuredWebhook.name, tenantHostname)
				: unsuspendWebhook(configuredWebhook.name, authToken);
		},
		{
			onSuccess: () => {
				client.invalidateQueries([
					QUERY_KEYS.WEBHOOK_CONFIGURATIONS,
					authTenantId,
				]);
				onCancel();
				successToast(UNSUSPEND_WEBHOOK_CONFIGURATION_SUCCESS());
			},
			onError: (err) => {
				if (axios.isAxiosError(err) && err?.response?.data?.message) {
					errorToast(
						UNSUSPEND_WEBHOOK_CONFIGURATION_FAILURE() +
							': ' +
							err.response.data.message,
					);
				} else {
					errorToast(UNSUSPEND_WEBHOOK_CONFIGURATION_FAILURE());
				}
			},
		},
	);

	return (
		<Dialog open={open} onClose={(e, r) => handleModalClose(r, onCancel)}>
			<DialogTitle>
				{CONFIRM_UNSUSPEND_WEBHOOK_CONFIGURATION_MODAL_HEADER_LABEL()}
			</DialogTitle>
			<DialogContent>
				<Stack alignItems='flex-start' direction='column' spacing={2}>
					{configuredWebhook.suspendedTimestamp && (
						<Typography>
							{CONFIRM_UNSUSPEND_WEBHOOK_CONFIGURATION_MODAL_SUSPENDED_ON(
								dayjs(parseInt(configuredWebhook.suspendedTimestamp)).format(
									WEBHOOK_CONFIGURATION_UNSUSPEND_DATE_FORMAT(),
								),
							)}
						</Typography>
					)}
					<Typography>
						{CONFIRM_UNSUSPEND_WEBHOOK_CONFIGURATION_MODAL_CONFIRM_MESSAGE(
							configuredWebhook.name,
						)}
					</Typography>
				</Stack>
			</DialogContent>
			<DialogActions sx={{ m: 2 }}>
				<Button onClick={onCancel} color='neutralButton'>
					{CONFIRM_UNSUSPEND_WEBHOOK_CONFIGURATION_MODAL_CANCEL_TEXT()}
				</Button>
				<Button
					color='secondary'
					variant='contained'
					onClick={() => onUnsuspend()}
				>
					{isLoading ? (
						<CircularProgress size={20} style={{ color: 'white' }} />
					) : (
						CONFIRM_UNSUSPEND_WEBHOOK_CONFIGURATION_MODAL_DELETE_TEXT()
					)}
				</Button>
			</DialogActions>
		</Dialog>
	);
}
